import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import { route } from "../../utils";
import {
  CreateIntegrationConnectionRequest,
  CreateIntegrationConnectionResponse,
  IntegrationConnection,
  IntegrationConnectionsResponse,
  UpdateIntegrationConnectionConfigRequest,
  UpdateIntegrationConnectionConfigResponse,
  UpdateIntegrationConnectionRequest,
} from "./types";

// Export types
export * from "./types";

const ENDPOINTS = {
  INTEGRATION_CONNECTIONS: {
    LIST: "/api/v2/org/:organization_id/user/:user_id/integration-connections",
    CREATE: "/api/v2/org/:organization_id/integration-connections",
    DELETE:
      "/api/v2/org/:organization_id/integration-connections/:integration_connection_id",
    UPDATE:
      "/api/v2/org/:organization_id/integration-connections/:integration_connection_id",
    UPDATE_CONFIG:
      "/api/v2/org/:organization_id/integration-connections/:integration_connection_id/config",
    USER_CREATE:
      "/api/v2/org/:organization_id/user/:user_id/integration-connections",
    USER_UPDATE:
      "/api/v2/org/:organization_id/user/:user_id/integration-connections/:integration_connection_id",
    USER_DELETE:
      "/api/v2/org/:organization_id/user/:user_id/integration-connections/:integration_connection_id",
    USER_UPDATE_CONFIG:
      "/api/v2/org/:organization_id/user/:user_id/integration-connections/:integration_connection_id/config",
  },
};

export class IntegrationConnectionsAPI {
  private client: CohesiveAPIClient;

  constructor(client: CohesiveAPIClient) {
    this.client = client;
  }

  // Integration Connection Methods

  /**
   * Get all integration connections
   */
  async list(config?: AxiosRequestConfig): Promise<IntegrationConnection[]> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.get<IntegrationConnectionsResponse>(
        route(ENDPOINTS.INTEGRATION_CONNECTIONS.LIST, {
          organization_id: orgId,
          user_id: userId,
        }),
        config,
      );

    return response.data.data.integration_connections.map((connection) => ({
      ...connection,
      integration_name: connection.integration_name.replace(/-/g, " "),
    }));
  }

  /**
   * Create integration connection
   */
  async create(
    data: CreateIntegrationConnectionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();

    const response =
      await this.client.axios.post<CreateIntegrationConnectionResponse>(
        route(ENDPOINTS.INTEGRATION_CONNECTIONS.CREATE, {
          organization_id: orgId || "",
        }),
        data,
        config,
      );

    return response.data.data.integration_connection;
  }

  /**
   * Delete integration connection
   */
  async delete(
    integrationConnectionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();

    await this.client.axios.delete(
      route(ENDPOINTS.INTEGRATION_CONNECTIONS.DELETE, {
        organization_id: orgId || "",
        integration_connection_id: integrationConnectionId,
      }),
      config,
    );
  }

  /**
   * Update integration connection
   */
  async update(
    integrationConnectionId: string,
    data: UpdateIntegrationConnectionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();

    const response = await this.client.axios.put(
      route(ENDPOINTS.INTEGRATION_CONNECTIONS.UPDATE, {
        organization_id: orgId || "",
        integration_connection_id: integrationConnectionId,
      }),
      data,
      config,
    );

    return response.data.integration_connection;
  }

  // User Integration Connection Methods

  /**
   * Create integration connection for current user
   */
  async createUserIntegrationConnection(
    data: CreateIntegrationConnectionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.post<CreateIntegrationConnectionResponse>(
        route(ENDPOINTS.INTEGRATION_CONNECTIONS.USER_CREATE, {
          organization_id: orgId || "",
          user_id: userId || "",
        }),
        data,
        config,
      );

    return response.data.data.integration_connection;
  }

  /**
   * Update user integration connection
   */
  async updateUserIntegrationConnection(
    integrationConnectionId: string,
    data: UpdateIntegrationConnectionRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response = await this.client.axios.put(
      route(ENDPOINTS.INTEGRATION_CONNECTIONS.USER_UPDATE, {
        organization_id: orgId || "",
        user_id: userId || "",
        integration_connection_id: integrationConnectionId,
      }),
      data,
      config,
    );

    return response.data.integration_connection;
  }

  /**
   * Delete user integration connection
   */
  async deleteUserIntegrationConnection(
    integrationConnectionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    await this.client.axios.delete(
      route(ENDPOINTS.INTEGRATION_CONNECTIONS.USER_DELETE, {
        organization_id: orgId || "",
        user_id: userId || "",
        integration_connection_id: integrationConnectionId,
      }),
      config,
    );
  }

  // Connection Config Methods

  /**
   * Update org-level connection config (admin only, full replacement).
   */
  async updateConnectionConfig(
    integrationConnectionId: string,
    data: UpdateIntegrationConnectionConfigRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();

    const response =
      await this.client.axios.put<UpdateIntegrationConnectionConfigResponse>(
        route(ENDPOINTS.INTEGRATION_CONNECTIONS.UPDATE_CONFIG, {
          organization_id: orgId || "",
          integration_connection_id: integrationConnectionId,
        }),
        data,
        config,
      );

    return response.data.data.integration_connection;
  }

  /**
   * Update user-level connection config (full replacement).
   */
  async updateUserConnectionConfig(
    integrationConnectionId: string,
    data: UpdateIntegrationConnectionConfigRequest,
    config?: AxiosRequestConfig,
  ): Promise<IntegrationConnection> {
    const orgId = await this.client.getOrgID();
    const userId = await this.client.getUserID();

    const response =
      await this.client.axios.put<UpdateIntegrationConnectionConfigResponse>(
        route(ENDPOINTS.INTEGRATION_CONNECTIONS.USER_UPDATE_CONFIG, {
          organization_id: orgId || "",
          user_id: userId || "",
          integration_connection_id: integrationConnectionId,
        }),
        data,
        config,
      );

    return response.data.data.integration_connection;
  }
}
